import type { AIModel } from '@/api/types'
import {
  filterCompatibleAudioModels,
  isElevenLabsMusicModel,
  isElevenLabsTTSModel,
  isMurekaMusicModel,
  isSeedAudioModel,
} from './audio-compat'
import { mergeGenParams } from './model-params'
import type { AudioNodeState, AudioProductTab, AudioTabState, OutputState, StudioNodeData } from './types'

export const AUDIO_PRODUCT_TABS: AudioProductTab[] = ['narration', 'voice_clone', 'voice_design', 'music']

/** LibTV 贴近版顶栏：配音 | 音乐 */
export type AudioTopTab = 'dubbing' | 'music'

/** 配音内音色来源（映射到内部 product tab） */
export type VoiceSource = 'system' | 'clone' | 'design'

export const AUDIO_TOP_TABS: AudioTopTab[] = ['dubbing', 'music']

export const AUDIO_TOP_TAB_LABEL_KEYS: Record<AudioTopTab, string> = {
  dubbing: 'canvas.audioTab.dubbing',
  music: 'canvas.audioTab.music',
}

export const VOICE_SOURCE_OPTIONS: { value: VoiceSource; labelKey: string }[] = [
  { value: 'system', labelKey: 'canvas.audioTab.voiceSourceSystem' },
  { value: 'clone', labelKey: 'canvas.audioTab.voiceSourceClone' },
  { value: 'design', labelKey: 'canvas.audioTab.voiceSourceDesign' },
]

export const AUDIO_TAB_LABEL_KEYS: Record<AudioProductTab, string> = {
  narration: 'canvas.audioTab.narration',
  voice_clone: 'canvas.audioTab.voiceClone',
  voice_design: 'canvas.audioTab.voiceDesign',
  music: 'canvas.audioTab.music',
}

export const AUDIO_TAB_HINT_KEYS: Partial<Record<AudioProductTab, string>> = {
  voice_clone: 'canvas.audioTab.hintClone',
  music: 'canvas.audioTab.hintMusic',
}

export function topTabFromProductTab(tab: AudioProductTab): AudioTopTab {
  return tab === 'music' ? 'music' : 'dubbing'
}

export function voiceSourceFromProductTab(tab: AudioProductTab): VoiceSource {
  if (tab === 'voice_clone') return 'clone'
  if (tab === 'voice_design') return 'design'
  return 'system'
}

export function productTabFromUi(top: AudioTopTab, source: VoiceSource = 'system'): AudioProductTab {
  if (top === 'music') return 'music'
  if (source === 'clone') return 'voice_clone'
  if (source === 'design') return 'voice_design'
  return 'narration'
}

/** 外置提示词框 placeholder（按内部 Tab / 音乐模式）。 */
export function audioPromptPlaceholderKey(
  tab: AudioProductTab,
  params?: Record<string, string | number>,
  modelId?: string,
): string {
  if (isSeedAudioModel(modelId)) return 'canvas.audioTab.promptSeedAudio'
  if (isElevenLabsTTSModel(modelId)) return 'canvas.audioTab.promptElevenTTS'
  if (isElevenLabsMusicModel(modelId)) return 'canvas.audioTab.promptElevenMusic'
  if (isMurekaMusicModel(modelId) && String(params?.music_mode ?? 'describe') === 'lyrics') {
    return 'canvas.audioTab.promptMurekaLyrics'
  }
  if (isMurekaMusicModel(modelId)) return 'canvas.audioTab.promptMurekaDescribe'
  if (tab === 'music') {
    return String(params?.music_mode ?? 'describe') === 'lyrics'
      ? 'canvas.audioTab.promptLyrics'
      : 'canvas.audioTab.promptMusicDescribe'
  }
  if (tab === 'voice_clone') return 'canvas.audioTab.promptClone'
  if (tab === 'voice_design') return 'canvas.audioTab.promptDesign'
  return 'canvas.audioTab.promptDubbing'
}

/** 根据 model id 推断所属产品 Tab（先匹配者优先）。 */
export function inferAudioProductGroup(modelId: string): AudioProductTab {
  const id = modelId.toLowerCase().trim()
  if (!id) return 'narration'
  if (
    isElevenLabsMusicModel(id) ||
    isMurekaMusicModel(id) ||
    id.includes('suno') ||
    id.includes('music-v1') ||
    id.startsWith('music-')
  ) {
    return 'music'
  }
  if (id.includes('qwen3-tts-vc') || id.includes('indextts')) return 'voice_clone'
  if (id.includes('qwen3-tts-vd')) return 'voice_design'
  return 'narration'
}

export function filterAudioModelsByTab(models: AIModel[], tab: AudioProductTab): AIModel[] {
  return filterCompatibleAudioModels(models).filter((m) => inferAudioProductGroup(m.id) === tab)
}

export function defaultModelForAudioTab(models: AIModel[], tab: AudioProductTab): string {
  const filtered = filterAudioModelsByTab(models, tab)
  if (!filtered.length) return ''
  if (tab === 'narration') {
    const qwen = filtered.find((m) => {
      const id = m.id.toLowerCase()
      return id.includes('qwen3-tts-flash') && !id.includes('vc') && !id.includes('vd') && !id.includes('instruct')
    })
    if (qwen) return qwen.id
  }
  return filtered[0]?.id ?? ''
}

export function createEmptyAudioTabState(tab: AudioProductTab, models: AIModel[], modelId?: string): AudioTabState {
  const resolvedModelId = modelId || defaultModelForAudioTab(models, tab)
  if (tab === 'music') {
    return {
      modelId: resolvedModelId,
      params: {
        music_mode: 'describe',
        mode: 'music',
        style: '',
        lyrics: '',
        duration: 60,
      },
      takes: [],
    }
  }
  // 配音三来源：勿带 mode=bgm 等通用回退，避免旁白 Tab 误显「背景音乐」
  return {
    modelId: resolvedModelId,
    params: {
      mode: 'tts',
      voice: 'female',
      speed: 1,
      pitch: 0,
    },
    takes: [],
  }
}

export function createInitialAudioNodeState(models: AIModel[], seed?: Partial<AudioNodeState>): AudioNodeState {
  const tabs = {} as Record<AudioProductTab, AudioTabState>
  for (const tab of AUDIO_PRODUCT_TABS) {
    tabs[tab] = seed?.tabs?.[tab] ?? createEmptyAudioTabState(tab, models)
  }
  const activeTab = seed?.activeTab ?? 'narration'
  return { activeTab, tabs }
}

export function getActiveAudioTab(data: StudioNodeData): AudioProductTab {
  return data.audio?.activeTab ?? 'narration'
}

export function getActiveAudioTabState(data: StudioNodeData): AudioTabState {
  const tab = getActiveAudioTab(data)
  const fromAudio = data.audio?.tabs?.[tab]
  if (fromAudio) return fromAudio
  return {
    modelId: data.gen?.modelId ?? '',
    params: data.gen?.params ?? {},
    takes: data.takes ?? [],
    preferredTakeVersion: data.preferredTakeVersion,
  }
}

export function outputFromAudioTabState(tabState: AudioTabState): OutputState {
  const takes = tabState.takes ?? []
  if (!takes.length) return { status: 'idle', dataType: 'audio' }
  const preferred = tabState.preferredTakeVersion
  const activeTake = takes.find((x) => x.version === preferred) ?? takes[takes.length - 1]
  if (!activeTake?.previewUrl && activeTake?.status !== 'running' && activeTake?.status !== 'pending') {
    return { status: 'idle', dataType: 'audio' }
  }
  const status =
    activeTake.status === 'succeeded'
      ? 'succeeded'
      : activeTake.status === 'failed'
        ? 'failed'
        : activeTake.status === 'running' || activeTake.status === 'pending'
          ? 'running'
          : 'idle'
  return {
    status,
    previewUrl: activeTake.previewUrl,
    dataType: 'audio',
  }
}

/** 补齐 audio 结构；加载旧画布或 models 晚到时调用。已完整则原样返回。 */
export function ensureAudioNodeData(data: StudioNodeData, audioModels: AIModel[]): StudioNodeData {
  if (data.nodeType !== 'gen_audio') return data

  if (data.audio) {
    const tabs = data.audio.tabs
    const allTabsReady = AUDIO_PRODUCT_TABS.every((tab) => !!tabs[tab])
    const active = tabs[data.audio.activeTab]
    const modelsReady = audioModels.length === 0 || !!active?.modelId
    if (allTabsReady && modelsReady) return data
  }

  let audio = data.audio
  if (!audio) {
    const legacyModelId = data.gen?.modelId ?? ''
    const legacyTab = legacyModelId ? inferAudioProductGroup(legacyModelId) : 'narration'
    audio = createInitialAudioNodeState(audioModels)
    audio.activeTab = legacyTab
    if (data.gen) {
      audio.tabs[legacyTab] = {
        modelId: data.gen.modelId,
        params: data.gen.params ?? mergeGenParams('gen_audio', {}),
        takes: data.takes ?? [],
        preferredTakeVersion: data.preferredTakeVersion,
      }
    }
  } else {
    const tabs = { ...audio.tabs }
    for (const tab of AUDIO_PRODUCT_TABS) {
      if (!tabs[tab]) tabs[tab] = createEmptyAudioTabState(tab, audioModels)
      else if (!tabs[tab].modelId) {
        tabs[tab] = { ...tabs[tab], modelId: defaultModelForAudioTab(audioModels, tab) }
      }
    }
    audio = { ...audio, tabs }
  }

  const active = audio.tabs[audio.activeTab] ?? audio.tabs.narration
  let resolved: StudioNodeData = {
    ...data,
    audio,
    gen: { modelId: active.modelId, params: active.params },
    takes: active.takes ?? [],
    preferredTakeVersion: active.preferredTakeVersion,
    output: outputFromAudioTabState(active),
  }

  // 切 Tab 必须内联 applyActiveAudioTab，禁止再调 switchAudioTab（后者会回呼本函数，造成栈溢出）。
  if (!active.modelId && audioModels.length > 0) {
    const fallbackTab = AUDIO_PRODUCT_TABS.find((tab) => defaultModelForAudioTab(audioModels, tab))
    if (fallbackTab && fallbackTab !== audio.activeTab) {
      resolved = { ...resolved, ...applyActiveAudioTab(audio, fallbackTab) }
    }
  }

  return resolved
}

function applyActiveAudioTab(audio: AudioNodeState, tab: AudioProductTab): Partial<StudioNodeData> {
  const target = audio.tabs[tab]
  return {
    audio: { ...audio, activeTab: tab },
    gen: { modelId: target.modelId, params: target.params },
    takes: target.takes ?? [],
    preferredTakeVersion: target.preferredTakeVersion,
    output: outputFromAudioTabState(target),
  }
}

export function patchAudioTab(
  data: StudioNodeData,
  tab: AudioProductTab,
  tabPatch: Partial<AudioTabState>,
  audioModels: AIModel[],
  opts?: { replaceParams?: boolean },
): Partial<StudioNodeData> {
  const base = ensureAudioNodeData(data, audioModels)
  const audio = base.audio!
  const nextTabState: AudioTabState = {
    ...audio.tabs[tab],
    ...tabPatch,
    params: tabPatch.params
      ? opts?.replaceParams
        ? tabPatch.params
        : { ...audio.tabs[tab].params, ...tabPatch.params }
      : audio.tabs[tab].params,
  }
  const nextTabs = { ...audio.tabs, [tab]: nextTabState }
  const nextAudio: AudioNodeState = { activeTab: audio.activeTab, tabs: nextTabs }

  if (tab !== audio.activeTab) {
    return { audio: nextAudio }
  }

  return {
    audio: nextAudio,
    gen: { modelId: nextTabState.modelId, params: nextTabState.params },
    takes: nextTabState.takes ?? [],
    preferredTakeVersion: nextTabState.preferredTakeVersion,
    output: outputFromAudioTabState(nextTabState),
  }
}

export function switchAudioTab(
  data: StudioNodeData,
  tab: AudioProductTab,
  audioModels: AIModel[],
): Partial<StudioNodeData> {
  const base = ensureAudioNodeData(data, audioModels)
  return applyActiveAudioTab(base.audio!, tab)
}

/** 切换模型：按 modelId 推断 product tab，必要时切 tab 并写入 modelId/params。 */
export function applyAudioModelSelection(
  data: StudioNodeData,
  modelId: string,
  params: Record<string, string | number>,
  audioModels: AIModel[],
): Partial<StudioNodeData> {
  const targetTab = inferAudioProductGroup(modelId)
  const base = ensureAudioNodeData(data, audioModels)
  const activeTab = getActiveAudioTab(base)
  const working =
    activeTab === targetTab
      ? base
      : ({ ...base, ...applyActiveAudioTab(base.audio!, targetTab) } as StudioNodeData)
  return patchAudioTab(working, targetTab, { modelId, params }, audioModels, { replaceParams: true })
}

export function appendAudioTabTake(
  data: StudioNodeData,
  tab: AudioProductTab,
  take: NonNullable<AudioTabState['takes']>[number],
  audioModels: AIModel[],
): Partial<StudioNodeData> {
  const base = ensureAudioNodeData(data, audioModels)
  const audio = base.audio!
  const tabState = audio.tabs[tab]
  const takes = [...(tabState.takes ?? []), take]
  const nextTab: AudioTabState = {
    ...tabState,
    takes,
    preferredTakeVersion: take.version,
  }
  const nextTabs = { ...audio.tabs, [tab]: nextTab }
  const patch: Partial<StudioNodeData> = {
    audio: { activeTab: audio.activeTab, tabs: nextTabs },
  }
  if (tab === audio.activeTab) {
    patch.takes = takes
    patch.preferredTakeVersion = take.version
    patch.output = {
      status: take.status === 'succeeded' ? 'succeeded' : 'failed',
      previewUrl: take.previewUrl,
      dataType: 'audio',
      error: take.status === 'failed' ? data.output?.error : undefined,
    }
  }
  return patch
}

/** 切换 Tab（若需要）并设为定版 take */
export function preferAudioTabTakeOnNode(
  data: StudioNodeData,
  tab: AudioProductTab,
  version: number,
  audioModels: AIModel[],
): Partial<StudioNodeData> {
  const base = ensureAudioNodeData(data, audioModels)
  const active = getActiveAudioTab(base)
  const switched = active !== tab && base.audio ? applyActiveAudioTab(base.audio, tab) : {}
  const merged = { ...base, ...switched }
  return { ...switched, ...preferAudioTabTake(merged, tab, version, audioModels) }
}

export function preferAudioTabTake(
  data: StudioNodeData,
  tab: AudioProductTab,
  version: number,
  audioModels: AIModel[],
): Partial<StudioNodeData> {
  const base = ensureAudioNodeData(data, audioModels)
  const audio = base.audio!
  const tabState = audio.tabs[tab]
  const take = tabState.takes?.find((x) => x.version === version)
  const nextTab: AudioTabState = { ...tabState, preferredTakeVersion: version }
  const nextTabs = { ...audio.tabs, [tab]: nextTab }
  const patch: Partial<StudioNodeData> = {
    audio: { activeTab: audio.activeTab, tabs: nextTabs },
  }
  if (tab === audio.activeTab && take) {
    patch.preferredTakeVersion = version
    patch.output = {
      status: take.status === 'succeeded' ? 'succeeded' : 'failed',
      previewUrl: take.previewUrl,
      dataType: 'audio',
    }
  }
  return patch
}

/** 从历史中移除指定 Tab 的 take（不调整定版；删除定版 take 须在调用前拦截） */
export function removeAudioTabTake(
  data: StudioNodeData,
  tab: AudioProductTab,
  version: number,
  audioModels: AIModel[],
): Partial<StudioNodeData> | null {
  const base = ensureAudioNodeData(data, audioModels)
  const audio = base.audio!
  const tabState = audio.tabs[tab]
  const takes = tabState.takes ?? []
  if (!takes.some((t) => t.version === version)) return null

  const nextTab: AudioTabState = { ...tabState, takes: takes.filter((t) => t.version !== version) }
  const nextTabs = { ...audio.tabs, [tab]: nextTab }
  const patch: Partial<StudioNodeData> = {
    audio: { activeTab: audio.activeTab, tabs: nextTabs },
  }
  if (tab === getActiveAudioTab(base)) {
    patch.takes = nextTab.takes
  }
  return patch
}
